"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RegionComponent = void 0;
/**
 * An abstract visual component to define a component of region presentation UI.
 */
class RegionComponent {
    /**
     * Creates a new UI component (part of the region).
     * @param paper - The `Snap.Paper` object to draw on.
     * @param paperRect - The parent bounding box for created component.
     * @param regionData - The `RegionData` object shared across components. Used also for initial setup.
     * @param callbacks - The external callbacks collection.
     */
    constructor(paper, paperRect, regionData, callbacks = null) {
        /**
         * Defines if the component is visible.
         */
        this.isVisible = true;
        /**
         * Defines if the component is in a frozen state.
         */
        this.isFrozen = false;
        /**
         * Defines if the component is selected.
         */
        this.isSelected = false;
        this.paper = paper;
        this.paperRect = paperRect;
        this.regionData = regionData;
        this.callbacks = callbacks;
    }
    /**
     * The `x`-coordinate of the component. Defined through the `regionsData`.
     */
    get x() {
        return this.regionData.x;
    }
    /**
     * The `y`-coordinate of the component. Defined through the `regionsData`.
     */
    get y() {
        return this.regionData.y;
    }
    /**
     * The `width` of the component. Defined through the `regionsData`.
     */
    get width() {
        return this.regionData.boundRect.width;
    }
    /**
     * The `height` of the component. Defined through the `regionsData`.
     */
    get height() {
        return this.regionData.boundRect.height;
    }
    /**
     * The `area` of the component. Defined through the `regionsData`.
     */
    get area() {
        return this.regionData.area;
    }
    /**
     * The `boundRect` of the component. Defined through the `regionsData`.
     * @remarks Returns the `Rect` object of the same `width` and `height` as the component.
     */
    get boundRect() {
        return this.regionData.boundRect;
    }
    /**
     * Switches the component presentation to the hidden state.
     */
    hide() {
        this.node.node.setAttribute("display", "none");
        this.isVisible = false;
    }
    /**
     * Switches the component presentation to the visible state.
     */
    show() {
        this.node.node.setAttribute("display", "inherit");
        this.isVisible = true;
    }
    /**
     * Selects the component.
     */
    select() {
        this.isSelected = true;
        this.node.addClass("selected");
    }
    /**
     * Unselects the component.
     */
    unselect() {
        this.isSelected = false;
        this.node.removeClass("selected");
    }
    /**
     * Switches the component to the frozen state.
     */
    freeze() {
        this.isFrozen = true;
    }
    /**
     * Switches the component to the unfrozen state.
     */
    unfreeze() {
        this.isFrozen = false;
    }
    move(arg1, arg2) {
        this.regionData.move(arg1, arg2);
        this.redraw();
    }
    /**
     * Resizes the component to specified `width` and `height`.
     * @param width - The new `width` for the component.
     * @param height - The new `height` for the component.
     */
    resize(width, height) {
        this.regionData.resize(width, height);
        this.redraw();
    }
    /**
     * Resizes the bounding box for the component.
     * @param width - The new `width` of the bounding box.
     * @param height - The new `height` of the bounding box.
     */
    resizePaper(width, height) {
        this.paperRect.resize(width, height);
    }
    /**
     * Subscribes the component elements according to provided event descriptors. Binds to the `this` object.
     * @param listeners - The collection of event descriptors.
     */
    subscribeToEvents(listeners) {
        listeners.forEach((e) => {
            e.base.addEventListener(e.event, this.makeFreezable(e.listener.bind(this), e.bypass));
        });
    }
    /**
     * A helper function to make event listeners frozen if the component state is frozen.
     * @param f - Function to wrap.
     * @param bypass - A flag whether event should bypass.
     */
    makeFreezable(f, bypass = false) {
        return (args) => {
            if (!this.isFrozen || bypass) {
                f(args);
            }
        };
    }
}
exports.RegionComponent = RegionComponent;
//# sourceMappingURL=RegionComponent.js.map